import React from 'react';
import PropTypes from 'prop-types';

import OutOfDeliveryModal from '@conversion/components/OutOfDeliveryModal';
import NoLocationPointToast from '@shopDetails/components/NoLocationPointToast';
import SelectedLocationPointToast from '@shopDetails/components/SelectedLocationPointToast';
import LocationParcel from '@location/containers/LocationParcel';
import { canOrder } from '@utils/shop';
import canInitNewCart from '@utils/cart/canInitNewCart';
import isValidCartAddress from '@utils/cart/isValidCartAddress';
import { NOTIFICATION_TIMEOUT } from '@shared/constants';
import { LOCATION_ORIGIN } from '@location/constants';
import { ignore } from '@utils/object';
import { USER_APPLICATION } from '@utils/constants';

const ownProps = [
  'cartHasLocation',
  'hasCart',
  'cartFromOrderId',
  'isInitializingCart',
  'showNoLocationPointToast',
  'isShowingOutOfDeliveryError',
  'shop',
  'cart',
  'searchedLocation',
  'shopName',
  'setSearchedLocation',
  'closeNoLocationPointToast',
  'initializeCart',
  'onChangeAddress',
  'navigateToShopsListAfterOutOfDelivery',
  // LocationFlowManager
  'getLocationByParcel',
];

function WithOnBeforeProductConfig(
  T,
  { unmount = true, closeOutOfDeliveryModal = false } = {},
) {
  return class WithOnBeforeProductConfigFn extends React.PureComponent {
    static propTypes = {
      cartHasLocation: PropTypes.bool.isRequired,
      hasCart: PropTypes.bool.isRequired,
      cartFromOrderId: PropTypes.number,
      isInitializingCart: PropTypes.bool.isRequired,
      showNoLocationPointToast: PropTypes.bool.isRequired,
      isShowingOutOfDeliveryError: PropTypes.bool.isRequired,
      shop: PropTypes.object,
      cart: PropTypes.object,
      searchedLocation: PropTypes.object,
      shopName: PropTypes.string,

      setSearchedLocation: PropTypes.func.isRequired,
      closeNoLocationPointToast: PropTypes.func.isRequired,
      initializeCart: PropTypes.func.isRequired,
      onChangeAddress: PropTypes.func.isRequired,
      navigateToShopsListAfterOutOfDelivery: PropTypes.func.isRequired,
      resetOutOfDeliveryError: PropTypes.func.isRequired,
      isDesktopLayout: PropTypes.bool.isRequired,
      businessTypeFromQuery: PropTypes.string,

      // LocationFlowManager
      show: PropTypes.bool.isRequired,
      getLocationByParcel: PropTypes.func.isRequired,

      // Deeplink
      shouldShowLocationPointToast: PropTypes.bool,
    };

    state = {
      showSpinnerWhileInitializingCart: false,
      updatingCartAddress: false,
      locationOutOfDelivery: null,
      showSelectedLocationPointToast: false,
      showLocationParcel: false,
    };

    componentDidMount() {
      this._isMounted = true;
    }

    componentWillUnmount() {
      this._isMounted = false;
    }

    componentDidUpdate() {
      const { isInitializingCart } = this.props;
      const { showSpinnerWhileInitializingCart } = this.state;
      if (
        showSpinnerWhileInitializingCart &&
        !isInitializingCart &&
        this.resolveInitializingCart
      ) {
        // Hide loading after cart initialized and continue with the product clicked flow.
        this.resolveInitializingCart();
        delete this.resolveInitializingCart;
        this.setShowSpinner(false);
      }
    }

    setStateIfMounted = (...params) => {
      if (this._isMounted) this.setState(...params);
    };

    setShowSpinner = showSpinnerWhileInitializingCart =>
      this.setStateIfMounted({ showSpinnerWhileInitializingCart });

    onBeforeProductConfig = async () => {
      const { isInitializingCart, searchedLocation } = this.props;

      if (isInitializingCart) {
        // Show loading while initializing cart, so we know what to do when
        // a product is clicked.
        // We await the promise so when it resolves we execute the code that follows.
        this.setShowSpinner(true);
        await new Promise(resolve => {
          this.resolveInitializingCart = resolve;
        });
      }

      // This two props MUST be initialized after the resolution of the above promise,
      // so these have the most updated values.
      const { hasCart, cartHasLocation } = this.props;
      if (!hasCart) return { success: false, error: true };
      if (cartHasLocation) return { success: true };

      const success = await this.onGetLocation(searchedLocation);
      return { success: !!success };
    };

    updateCartAddressFinished = ({
      success,
      location,
      setSearchedLocation,
    }) => {
      if (success || setSearchedLocation) {
        this.props.setSearchedLocation(location);
        this.setStateIfMounted({ showSelectedLocationPointToast: true });
        setTimeout(
          () =>
            this.setStateIfMounted({ showSelectedLocationPointToast: false }),
          NOTIFICATION_TIMEOUT,
        );
      } else {
        this.setStateIfMounted({ locationOutOfDelivery: location });
      }
    };

    updateCartAddress = async location => {
      this.setStateIfMounted({ updatingCartAddress: true });
      const {
        onChangeAddress,
        cart,
        initializeCart,
        shop,
        cartFromOrderId,
        closeNoLocationPointToast,
        isDesktopLayout,
        businessTypeFromQuery,
      } = this.props;
      let setSearchedLocation = false;
      let success = false;

      if (canInitNewCart({ cart, shop })) {
        success = await initializeCart({
          restaurantId: shop.id,
          location,
          fromOrderId: cartFromOrderId,
          userApplication: isDesktopLayout
            ? USER_APPLICATION.WEB_DESKTOP
            : USER_APPLICATION.WEB_MOBILE,
          businessType: businessTypeFromQuery || shop?.businessType,
        });
      } else if (!canOrder(shop)) {
        setSearchedLocation = true;
      } else {
        success = await onChangeAddress({ address: location });
      }
      this.updateCartAddressFinished({
        success,
        location,
        setSearchedLocation,
      });

      closeNoLocationPointToast();
      this.setStateIfMounted({ updatingCartAddress: false });

      return success;
    };

    showOverlaySpinner() {
      const { isShowingOutOfDeliveryError } = this.props;
      const {
        updatingCartAddress,
        showSpinnerWhileInitializingCart,
      } = this.state;

      return (
        (updatingCartAddress && !isShowingOutOfDeliveryError) ||
        showSpinnerWhileInitializingCart
      );
    }

    showSearchForAddressButton() {
      const { locationOutOfDelivery } = this.state;
      const { searchedLocation } = this.props;
      return Boolean(locationOutOfDelivery || searchedLocation);
    }

    navigateToResults = () => {
      const {
        navigateToShopsListAfterOutOfDelivery,
        searchedLocation,
      } = this.props;
      const { locationOutOfDelivery } = this.state;

      const location = searchedLocation || locationOutOfDelivery;

      navigateToShopsListAfterOutOfDelivery(location);
    };

    onHideSelectedLocationPointToast = () =>
      this.setStateIfMounted({ showSelectedLocationPointToast: false });

    onGetLocation = async searchedLocation => {
      const { shopName, getLocationByParcel } = this.props;

      let location;
      if (isValidCartAddress(searchedLocation)) {
        location = searchedLocation;
      } else {
        location = await getLocationByParcel({
          origin: LOCATION_ORIGIN.SEARCH,
          shouldSetSearchedLocation: false, // Is set only if this.updateCartAddress succeded.
          subtitle: `Queremos verificar que ${shopName} entrega en tu ubicación`,
        });
      }

      return location ? this.updateCartAddress(location) : false;
    };

    onGetPromiseLocation = async () => {
      this.setStateIfMounted({ showLocationParcel: true });
      return new Promise((resolve, reject) => {
        this.resolveFlow = resolve;
        this.rejectFlow = reject;
      });
    };

    onFinishLocation = location => {
      if (this.resolveFlow) {
        this.resolveFlow(location ? this.updateCartAddress(location) : false);
        this.resolveFlow = null;
        this.setStateIfMounted({ showLocationParcel: false });
      }
    };

    onCancelParcel = () => {
      if (this.resolveFlow) {
        this.rejectFlow();
        this.resolveFlow = null;
        this.setStateIfMounted({ showLocationParcel: false });
      }
    };

    getPropsToInyect() {
      return ignore(this.props, ownProps);
    }

    render() {
      const { show } = this.props;
      if (!show && unmount) return null;

      const {
        isShowingOutOfDeliveryError,
        shopName,
        closeNoLocationPointToast,
        showNoLocationPointToast,
        resetOutOfDeliveryError,
        shouldShowLocationPointToast,
      } = this.props;
      const { showSelectedLocationPointToast, showLocationParcel } = this.state;

      return (
        <>
          <T
            loading={this.showOverlaySpinner()}
            onBeforeProductConfig={this.onBeforeProductConfig}
            {...this.getPropsToInyect()}
          />

          {show && (
            <>
              <OutOfDeliveryModal
                open={isShowingOutOfDeliveryError}
                onChangeAddressClick={this.onGetPromiseLocation}
                onSearchForAddressClick={this.navigateToResults}
                showSearchForAddress={this.showSearchForAddressButton()}
                onClose={
                  closeOutOfDeliveryModal ? resetOutOfDeliveryError : undefined
                }
              />

              <NoLocationPointToast
                open={shouldShowLocationPointToast && showNoLocationPointToast}
                onClick={this.onGetPromiseLocation}
                onClose={closeNoLocationPointToast}
              />

              <SelectedLocationPointToast
                open={showSelectedLocationPointToast}
                shopName={shopName}
                onClose={this.onHideSelectedLocationPointToast}
              />

              {showLocationParcel && (
                <LocationParcel
                  show={showLocationParcel}
                  onSuccess={this.onFinishLocation}
                  onCancel={this.onCancelParcel}
                />
              )}
            </>
          )}
        </>
      );
    }
  };
}

export default WithOnBeforeProductConfig;
